import { User, Check, AlertCircle } from 'react-feather';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import { formatPhoneNumber } from '../../utils/formatters';

const EMPTY_PROFILE = {
  businessName: '',
  contactName: '',
  contactPhone: '+1',
  addressLine: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  website: '',
  email: ''
};

/* The backend stores one free-text `business_address` column, and signup
   composes it as a comma-joined "street, city, province, postal, country".
   tax_service._extract_province_from_address then scans that string for a
   standalone 2-letter code to decide sales tax.

   So the split below is a presentation change only: the parts are joined
   back into exactly that order and separator before the PUT, and parsed out
   of it on load. Reordering the join or changing the separator would break
   province detection and silently zero a customer's tax. */
const ADDRESS_PARTS = ['addressLine', 'city', 'region', 'postalCode', 'country'];

const composeAddress = (profile) =>
  ADDRESS_PARTS.map((key) => profile[key].trim())
    .filter(Boolean)
    .join(', ');

/* Load is best-effort: the stored value may predate this form, or have come
   from Helcim with a street_2 we have no field for. Five-or-more parts fold
   the extras into the street line so nothing is dropped; anything shorter or
   unrecognisable lands wholly in the street line, which is the one place a
   user can see and correct it. */
const parseAddress = (value) => {
  const empty = { addressLine: '', city: '', region: '', postalCode: '', country: '' };
  if (!value || !value.trim()) return empty;

  const parts = value.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 5) return { ...empty, addressLine: value.trim() };

  // The trailing four are city/region/postal/country; everything before them
  // is the street, which is where a street_2 would have been joined in.
  const [country, postalCode, region, city] = [
    parts.pop(),
    parts.pop(),
    parts.pop(),
    parts.pop()
  ];
  return { addressLine: parts.join(', '), city, region, postalCode, country };
};

/* Field order follows the settings convention: identity first, then how to
   reach the business, then billing. Single column throughout, the previous
   two-column grid left "Website" orphaned in a half-width cell, and
   multi-column forms measurably slow completion. */
/* `half: true` pairs a field with its neighbour on one row. Following the
   Tailwind UI settings reference: pair fields whose values are naturally
   short (names, phone), keep anything that can run long, email, website,
   address, on its own full-width row so the value is never truncated. */
const FIELDS = [
  {
    name: 'businessName',
    label: 'Business name',
    type: 'text',
    placeholder: 'Acme Inc.',
    autoComplete: 'organization',
    half: true
  },
  {
    name: 'contactName',
    label: 'Contact name',
    type: 'text',
    placeholder: 'Jane Smith',
    autoComplete: 'name',
    half: true
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'you@company.com',
    autoComplete: 'email',
    half: true
  },
  {
    name: 'contactPhone',
    label: 'Contact phone',
    type: 'tel',
    placeholder: '+1 (555) 555-5555',
    autoComplete: 'tel',
    half: true
  },
  {
    name: 'website',
    label: 'Website',
    type: 'text',
    placeholder: 'https://example.com',
    autoComplete: 'url'
  },
  /* Billing address, split into its parts. One free-text line asked the user
     to remember an order and separator, and any deviation from it broke the
     province parse that sets sales tax. Named parts also fill the column
     that the single line left mostly empty beside the security cards. */
  {
    name: 'addressLine',
    label: 'Address line',
    type: 'text',
    placeholder: '123 Main St, Suite 400',
    autoComplete: 'street-address',
    optional: true
  },
  {
    name: 'city',
    label: 'City',
    type: 'text',
    placeholder: 'Toronto',
    autoComplete: 'address-level2',
    optional: true,
    half: true
  },
  {
    name: 'region',
    label: 'State / Province',
    type: 'text',
    placeholder: 'ON',
    autoComplete: 'address-level1',
    optional: true,
    half: true
  },
  {
    name: 'postalCode',
    label: 'Postal / ZIP code',
    type: 'text',
    placeholder: 'M5V 2T6',
    autoComplete: 'postal-code',
    optional: true,
    half: true
  },
  {
    name: 'country',
    label: 'Country',
    type: 'text',
    placeholder: 'Canada',
    autoComplete: 'country-name',
    optional: true,
    half: true
  }
];

const ProfileSection = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // 'saved' drives the transient confirmation; 'loadError'/'saveError' are
  // separate because a failed load and a failed save need different copy.
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  // Baseline for the dirty check. Without it the save button is always
  // enabled and the user gets no signal about whether changes are pending.
  const [saved_profile, setSavedProfile] = useState(EMPTY_PROFILE);

  const loadProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoadError('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/user-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      const next = {
        businessName: data.business_name || '',
        contactName: data.contact_name || '',
        contactPhone: data.contact_phone || '+1',
        ...parseAddress(data.business_address),
        website: data.business_url || '',
        email: data.email || ''
      };

      setProfile(next);
      setSavedProfile(next);
    } catch (error) {
      // Previously this was swallowed by a console.error and the section
      // rendered an empty form, which looks like "your profile is blank"
      // rather than "we could not load it".
      console.error('Error fetching profile:', error);
      setLoadError('We could not load your profile. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const isDirty = useMemo(
    () => FIELDS.some(({ name }) => profile[name] !== saved_profile[name]),
    [profile, saved_profile]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === 'contactPhone' ? formatPhoneNumber(value) : value;

    setProfile((prev) => ({ ...prev, [name]: newValue }));
    // Any edit invalidates the previous result, leaving a stale "Saved"
    // next to modified fields misreports the state.
    setSaved(false);
    setSaveError('');
  };

  const handleReset = () => {
    setProfile(saved_profile);
    setSaved(false);
    setSaveError('');
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (!userId || saving || !isDirty) return;

    setSaving(true);
    setSaved(false);
    setSaveError('');

    try {
      const token = localStorage.getItem('access_token');
      const apToken = localStorage.getItem('ap_token');
      const response = await fetch(`${API_BASE_URL}/api/user-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(apToken && { 'X-AP-Token': apToken })
        },
        body: JSON.stringify({
          business_name: profile.businessName,
          contact_name: profile.contactName,
          contact_phone: profile.contactPhone,
          business_address: composeAddress(profile),
          business_url: profile.website,
          email: profile.email
        })
      });

      if (!response.ok) {
        let detail = '';
        try {
          const body = await response.json();
          detail = body.detail || body.message || '';
        } catch {
          /* non-JSON error body, fall through to the generic message */
        }
        throw new Error(detail || 'Failed to save your profile. Please try again.');
      }

      // The new baseline is what we just sent, so the form goes clean and
      // the save button returns to disabled.
      setSavedProfile(profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveError(error.message || 'Failed to save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="account-section-card" aria-busy="true">
        <div className="account-section-header">
          <span className="account-section-icon" aria-hidden="true">
            <User />
          </span>
          <div className="account-section-heading">
            <h2 className="account-section-title">Profile</h2>
            <p className="account-section-description">Loading your profile information…</p>
          </div>
        </div>
        <div className="account-section-body">
          <div className="account-field-group">
            {/* Mirrors the real field layout, two paired rows then a full
                width one, so the card does not reflow when data arrives. */}
            <div className="account-field half">
              <div className="account-skeleton-line" style={{ width: '40%' }} />
              <div className="account-skeleton-field" />
            </div>
            <div className="account-field half">
              <div className="account-skeleton-line" style={{ width: '40%' }} />
              <div className="account-skeleton-field" />
            </div>
            <div className="account-field">
              <div className="account-skeleton-line" style={{ width: '25%' }} />
              <div className="account-skeleton-field" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="account-section-card">
      <div className="account-section-header">
        <span className="account-section-icon" aria-hidden="true">
          <User />
        </span>
        <div className="account-section-heading">
          <h2 className="account-section-title">Profile</h2>
          <p className="account-section-description">
            Business and contact details shown on invoices and used for account notifications.
          </p>
        </div>
      </div>

      <form onSubmit={handleSaveChanges} noValidate>
        <div className="account-section-body">
          {loadError && (
            <div className="account-banner error" role="alert">
              <AlertCircle aria-hidden="true" />
              <span>{loadError}</span>
            </div>
          )}

          <div className="account-field-group">
            {/* No hint lines and no "Optional" badges: the labels and
                placeholders already say what each field wants, and a caption
                under every input is what made this column feel loose. */}
            {FIELDS.map(({ name, label, type, placeholder, autoComplete, half, optional }) => (
              <div className={`account-field${half ? ' half' : ''}`} key={name}>
                <label htmlFor={`profile-${name}`}>{label}</label>
                <input
                  id={`profile-${name}`}
                  type={type}
                  name={name}
                  value={profile[name]}
                  onChange={handleInputChange}
                  placeholder={placeholder}
                  autoComplete={autoComplete}
                  required={!optional}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="account-section-footer">
          {/* aria-live so the outcome is announced; the visual status sits
              beside the button because that is where attention already is. */}
          <span className="account-footer-status-wrap" role="status" aria-live="polite">
            {saveError && (
              <span className="account-footer-status error">
                <AlertCircle aria-hidden="true" />
                {saveError}
              </span>
            )}
            {!saveError && saved && (
              <span className="account-footer-status success">
                <Check aria-hidden="true" />
                Changes saved
              </span>
            )}
            {!saveError && !saved && isDirty && (
              <span className="account-footer-status pending">Unsaved changes</span>
            )}
          </span>

          {isDirty && !saving && (
            <button type="button" className="secondary-button" onClick={handleReset}>
              Cancel
            </button>
          )}
          <button type="submit" className="primary-button" disabled={!isDirty || saving}>
            {saving && <span className="account-spinner" aria-hidden="true" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default ProfileSection;